import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { UsersStore } from '../../../pages/settings/users-teams/users/users.store';
import { UserEffects } from '../../../state/user/user.effects';
import { EUserTheme } from '../../../state/user/user.interface';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public activeTheme: EUserTheme = EUserTheme.light;
  public darkThemeClass = 'dbo-dark-theme';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public readonly store: UsersStore,
  ) {}

  public initTheme(): void {
    this.readAndClearLocalStorage();

    const currentUser = UserEffects.getUser();

    this.activeTheme = currentUser?.configuration?.theme || EUserTheme.light;

    this.setVisualTheme(this.activeTheme);
  }

  public toggleTheme(): void {
    this.activeTheme = this.activeTheme === EUserTheme.dark ? EUserTheme.light : EUserTheme.dark;

    try {
      if ('startViewTransition' in this.document) {
        this.document.startViewTransition(() => {
          this.applyAndSaveTheme();
        });
      } else {
        this.applyAndSaveTheme();
      }
    } catch {
      this.applyAndSaveTheme();
    }
  }

  private applyAndSaveTheme(): void {
    this.setVisualTheme(this.activeTheme);
    this.updateUser(this.activeTheme);
  }

  private setVisualTheme(theme: EUserTheme): void {
    this.updateDocumentTheme(this.document, theme);

    const iframes = this.document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

        if (iframeDoc) {
          this.updateDocumentTheme(iframeDoc, theme);
        }
      } catch (error) {
        console.warn(error);
      }
    });
  }

  private updateDocumentTheme(doc: Document, theme: EUserTheme): void {
    if (!doc.documentElement.classList.contains('digital-boards')) {
      return;
    }

    this.applyBootstrapAttr(doc, theme);

    if (theme === EUserTheme.dark) {
      doc.documentElement.classList.add(this.darkThemeClass);
    } else {
      doc.documentElement.classList.remove(this.darkThemeClass);
    }
  }

  private updateUser(theme: EUserTheme): void {
    const currentUser = UserEffects.getUser();

    if (currentUser && currentUser.id) {
      this.store.editUserData({
        dto: { configuration: { theme } },
        id: currentUser.id,
      });
    }
  }

  private applyBootstrapAttr(doc: Document, theme: EUserTheme): void {
    doc.documentElement.setAttribute('data-bs-theme', theme === EUserTheme.dark ? 'dark' : 'light');
  }

  /**
   * @deprecated Temporary migration to move legacy theme from local storage to the DB.
   * To be removed once the transition is complete.
   */
  private readAndClearLocalStorage(): void {
    const localStorageName = 'user-theme';
    const localTheme = localStorage.getItem(localStorageName);

    if (localTheme) {
      this.activeTheme = localTheme === this.darkThemeClass ? EUserTheme.dark : EUserTheme.light;
      this.updateUser(this.activeTheme);

      localStorage.removeItem(localStorageName);
    }
  }
}
